import { SerializedError } from '@reduxjs/toolkit';
import { useEffect, useMemo } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ResolutionSearchArticlesQuery } from '../apis/graphQl/elevio/grappa/types/grappa.types';
import { elevioActions } from '../elevio.actions';
import { elevioSelectors } from '../elevio.reducer/selectors';
import { getUrl } from '../utils';
import { elevioApi } from '../utils/apis/graphql/api';

const paging = {
  first: 10,
  offset: 0,
};

type UseSearchArticlesInResolutionParams = {
  searchTerm: string;
};
type UseSearchArticlesInResolutionError = SerializedError | undefined;

type UseSearchArticlesInResolution = {
  data: ResolutionSearchArticlesQuery | undefined;
  isFetching: boolean;
  error: UseSearchArticlesInResolutionError;
};
export function useSearchArticlesInResolution({
  searchTerm,
}: UseSearchArticlesInResolutionParams): UseSearchArticlesInResolution {
  const dispatch = useAppDispatch();
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  const searchArticlesQueryParams = useMemo(
    () => ({
      paging,
      terms: searchTerm,
    }),
    [searchTerm],
  );
  const url = getUrl();
  const pageUrl = useMemo(() => url, [url]);
  const { data, isFetching, error } =
    elevioApi.grappa.useResolutionSearchArticlesQuery({
      languageId,
      pageUrl: pageUrl,
      query: searchArticlesQueryParams,
    });

  const articleIds = useMemo(
    () => data?.searchArticles?.results?.map((article) => article.id),
    [data?.searchArticles?.results],
  );

  useEffect(() => {
    if (articleIds) {
      /**
       * Ideally this action call would be taken care of in a saga instead of
       * here.
       *
       * Rtk-query fires off the exact same actions for each of the queries in
       * an api. The only way to differentiate them is to parse the payload and
       * check if it matches a particular schema, when you would then yield* put
       * your action.
       *
       * It seemed like a very inefficient way to handle this problem- we would
       * need to do a check of the payload every single time a grappa api call
       * is made to see if we need to call this action.
       *
       * Doing it here in the useEffect, we only fire off the action a single
       * time when we know the data has updated - more efficient.
       */
      dispatch(
        elevioActions.resolution.setResolutionArticleIds({
          articleIds,
        }),
      );
    }
  }, [articleIds, dispatch]);

  return {
    data,
    isFetching,
    error: error as UseSearchArticlesInResolutionError,
  };
}
